import React from "react";
import { Loader2, ExternalLink, Save, Plus, Trash2, GripVertical } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import { Label } from "src/components/ui/label";
import { Switch } from "src/components/ui/switch";
import { useToast } from "src/components/ui/toast";
import { useHomepageEditor } from "src/features/website/hooks/useHomepageEditor";
import { ImageField } from "src/features/website/components/ImageField";

const SECTION_LABELS = {
  hero: "Hero", stats: "Stats band", services: "Services", offer_cards: "Offer cards",
  featured: "Featured listings", about: "About", portfolio: "Previous work",
  testimonials: "Testimonials", contact: "Contact",
};

export default function HomepageEditorPage() {
  const { toast } = useToast();
  const ed = useHomepageEditor();

  if (ed.loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!ed.content) {
    return <div className="p-8 text-sm text-red-600">Could not load homepage content.</div>;
  }

  const c = ed.content;
  const setSection = (key, patch) => ed.patch((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  const toggleSection = (key, on) =>
    ed.patch((prev) => ({ ...prev, sections: { ...prev.sections, [key]: { enabled: on } } }));
  const enabled = (key) => !!c.sections?.[key]?.enabled;

  const onSave = async () => {
    try {
      await ed.save();
      toast({ title: "Homepage saved", description: "Your public site has been updated." });
    } catch {
      toast({ variant: "destructive", title: "Save failed", description: "Please try again." });
    }
  };

  return (
    <div className="mx-auto max-w-[900px] pb-24">
      <div className="sticky top-0 z-10 -mx-4 mb-6 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Website homepage</h1>
          <p className="text-xs text-slate-500">Everything here shows on your public site. Toggle sections on/off, edit text, and upload images.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-600 hover:border-slate-300">
            <ExternalLink className="h-3.5 w-3.5" /> Preview
          </a>
          <Button onClick={onSave} disabled={ed.saving} className="gap-1.5">
            {ed.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {ed.saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {/* HERO */}
        <SectionCard title={SECTION_LABELS.hero} on={enabled("hero")} onToggle={(v) => toggleSection("hero", v)}>
          <Field label="Eyebrow"><Input value={c.hero?.eyebrow || ""} onChange={(e) => setSection("hero", { eyebrow: e.target.value })} /></Field>
          <Field label="Headline"><Input value={c.hero?.headline || ""} onChange={(e) => setSection("hero", { headline: e.target.value })} /></Field>
          <Field label="Sub-copy"><Textarea rows={3} value={c.hero?.subcopy || ""} onChange={(e) => setSection("hero", { subcopy: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <CtaEditor label="Primary button" value={c.hero?.primary_cta} onChange={(v) => setSection("hero", { primary_cta: v })} />
            <CtaEditor label="Secondary button" value={c.hero?.secondary_cta} onChange={(v) => setSection("hero", { secondary_cta: v })} />
          </div>
          <Field label="Image beside the headline (optional)">
            <div className="max-w-[280px]">
              <ImageField url={ed.mediaUrl("hero_side_image")} label="hero image" aspect="aspect-[4/5]"
                onSelect={(f) => ed.stageMedia("hero_side_image", f)}
                onClear={() => ed.stageMedia("hero_side_image", null)} />
            </div>
          </Field>
        </SectionCard>

        {/* STATS */}
        <SectionCard title={SECTION_LABELS.stats} on={enabled("stats")} onToggle={(v) => toggleSection("stats", v)}>
          <p className="text-[13px] text-slate-500">Shows live counts of your published listings and cities. No fields to edit.</p>
        </SectionCard>

        {/* SERVICES */}
        <SectionCard title={SECTION_LABELS.services} on={enabled("services")} onToggle={(v) => toggleSection("services", v)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Eyebrow"><Input value={c.services?.eyebrow || ""} onChange={(e) => setSection("services", { eyebrow: e.target.value })} /></Field>
            <Field label="Title"><Input value={c.services?.title || ""} onChange={(e) => setSection("services", { title: e.target.value })} /></Field>
          </div>
          <ItemsEditor
            items={c.services?.items || []}
            onChange={(items) => setSection("services", { items })}
            addLabel="Add service"
            makeItem={() => ({ title: "New service", body: "", image_key: null })}
            renderItem={(item, upd, idx) => (
              <div className="flex gap-3">
                <div className="w-[110px] shrink-0">
                  <ImageField url={ed.libraryUrl(item.image_key)} label="image" aspect="aspect-square"
                    onSelect={(f) => { const key = item.image_key || `svc_${Date.now()}_${idx}`; ed.stageLibrary(key, f); upd({ image_key: key }); }}
                    onClear={() => upd({ image_key: null })} />
                </div>
                <div className="flex-1 space-y-2">
                  <Input value={item.title || ""} onChange={(e) => upd({ title: e.target.value })} placeholder="Service title" />
                  <Textarea rows={2} value={item.body || ""} onChange={(e) => upd({ body: e.target.value })} placeholder="Short description" />
                </div>
              </div>
            )}
          />
        </SectionCard>

        {/* OFFER CARDS */}
        <SectionCard title={SECTION_LABELS.offer_cards} on={enabled("offer_cards")} onToggle={(v) => toggleSection("offer_cards", v)}>
          <p className="text-[13px] text-slate-500">Quick links to New / Resale / Rental properties, with optional images.</p>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {["new", "resale", "rental"].map((state) => (
              <div key={state}>
                <div className="mb-1 text-[12px] font-medium capitalize text-slate-600">{state}</div>
                <ImageField url={ed.libraryUrl(`offer_${state}`)} label="image" aspect="aspect-[3/2]"
                  onSelect={(f) => ed.stageLibrary(`offer_${state}`, f)}
                  onClear={() => {}} />
              </div>
            ))}
          </div>
        </SectionCard>

        {/* FEATURED / PORTFOLIO (info only — driven by listings) */}
        <SectionCard title={SECTION_LABELS.featured} on={enabled("featured")} onToggle={(v) => toggleSection("featured", v)}>
          <p className="text-[13px] text-slate-500">Shows your latest published listings automatically.</p>
        </SectionCard>
        <SectionCard title={SECTION_LABELS.portfolio} on={enabled("portfolio")} onToggle={(v) => toggleSection("portfolio", v)}>
          <p className="text-[13px] text-slate-500">Shows listings you’ve marked “Feature in Previous work”.</p>
        </SectionCard>

        {/* ABOUT */}
        <SectionCard title={SECTION_LABELS.about} on={enabled("about")} onToggle={(v) => toggleSection("about", v)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Eyebrow"><Input value={c.about?.eyebrow || ""} onChange={(e) => setSection("about", { eyebrow: e.target.value })} /></Field>
            <Field label="Title"><Input value={c.about?.title || ""} onChange={(e) => setSection("about", { title: e.target.value })} /></Field>
          </div>
          <Field label="Body (basic HTML allowed)">
            <Textarea rows={4} value={c.about?.body_html || ""} onChange={(e) => setSection("about", { body_html: e.target.value })} className="font-mono text-[13px]" />
          </Field>
          <ItemsEditor
            items={c.about?.highlights || []}
            onChange={(highlights) => setSection("about", { highlights })}
            addLabel="Add highlight"
            makeItem={() => ({ title: "New highlight", body: "" })}
            renderItem={(item, upd) => (
              <div className="space-y-2">
                <Input value={item.title || ""} onChange={(e) => upd({ title: e.target.value })} placeholder="Highlight title" />
                <Input value={item.body || ""} onChange={(e) => upd({ body: e.target.value })} placeholder="Short line" />
              </div>
            )}
          />
        </SectionCard>

        {/* TESTIMONIALS */}
        <SectionCard title={SECTION_LABELS.testimonials} on={enabled("testimonials")} onToggle={(v) => toggleSection("testimonials", v)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Eyebrow"><Input value={c.testimonials?.eyebrow || ""} onChange={(e) => setSection("testimonials", { eyebrow: e.target.value })} /></Field>
            <Field label="Title"><Input value={c.testimonials?.title || ""} onChange={(e) => setSection("testimonials", { title: e.target.value })} /></Field>
          </div>
          <ItemsEditor
            items={c.testimonials?.items || []}
            onChange={(items) => setSection("testimonials", { items })}
            addLabel="Add testimonial"
            makeItem={() => ({ quote: "", name: "", detail: "", avatar_key: null })}
            renderItem={(item, upd, idx) => (
              <div className="flex gap-3">
                <div className="w-[70px] shrink-0">
                  <ImageField url={ed.libraryUrl(item.avatar_key)} label="photo" aspect="aspect-square" size="rounded-full"
                    onSelect={(f) => { const key = item.avatar_key || `tst_${Date.now()}_${idx}`; ed.stageLibrary(key, f); upd({ avatar_key: key }); }}
                    onClear={() => upd({ avatar_key: null })} />
                </div>
                <div className="flex-1 space-y-2">
                  <Textarea rows={2} value={item.quote || ""} onChange={(e) => upd({ quote: e.target.value })} placeholder="What they said" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={item.name || ""} onChange={(e) => upd({ name: e.target.value })} placeholder="Name" />
                    <Input value={item.detail || ""} onChange={(e) => upd({ detail: e.target.value })} placeholder="Role / location (optional)" />
                  </div>
                </div>
              </div>
            )}
          />
        </SectionCard>

        {/* CONTACT */}
        <SectionCard title={SECTION_LABELS.contact} on={enabled("contact")} onToggle={(v) => toggleSection("contact", v)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Title"><Input value={c.contact?.title || ""} onChange={(e) => setSection("contact", { title: e.target.value })} /></Field>
            <Field label="Sub-copy"><Input value={c.contact?.subcopy || ""} onChange={(e) => setSection("contact", { subcopy: e.target.value })} /></Field>
          </div>
          <Field label="Address"><Textarea rows={2} value={c.contact?.address || ""} onChange={(e) => setSection("contact", { address: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Business hours"><Input value={c.contact?.hours || ""} onChange={(e) => setSection("contact", { hours: e.target.value })} /></Field>
            <Field label="Phone"><Input value={c.contact?.phone || ""} onChange={(e) => setSection("contact", { phone: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Email"><Input value={c.contact?.email || ""} onChange={(e) => setSection("contact", { email: e.target.value })} /></Field>
            <Field label="WhatsApp number"><Input value={c.contact?.whatsapp || ""} onChange={(e) => setSection("contact", { whatsapp: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Instagram URL"><Input value={c.contact?.instagram_url || ""} onChange={(e) => setSection("contact", { instagram_url: e.target.value })} /></Field>
            <Field label="Instagram handle"><Input value={c.contact?.instagram_handle || ""} onChange={(e) => setSection("contact", { instagram_handle: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Facebook URL"><Input value={c.contact?.facebook_url || ""} onChange={(e) => setSection("contact", { facebook_url: e.target.value })} placeholder="https://facebook.com/yourpage" /></Field>
            <Field label="Facebook page name"><Input value={c.contact?.facebook_handle || ""} onChange={(e) => setSection("contact", { facebook_handle: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="YouTube URL"><Input value={c.contact?.youtube_url || ""} onChange={(e) => setSection("contact", { youtube_url: e.target.value })} placeholder="https://youtube.com/@YourChannel" /></Field>
            <Field label="Google Business URL"><Input value={c.contact?.google_business_url || ""} onChange={(e) => setSection("contact", { google_business_url: e.target.value })} placeholder="https://g.page/... or Maps listing link" /></Field>
          </div>
          <Field label="Google Maps embed URL">
            <Input value={c.contact?.map_embed_url || ""} onChange={(e) => setSection("contact", { map_embed_url: e.target.value })}
              placeholder="https://maps.google.com/maps?q=...&output=embed" />
          </Field>
        </SectionCard>
      </div>
    </div>
  );
}

// ── Building blocks ──────────────────────────────────────────────────────────

function SectionCard({ title, on, onToggle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
        <label className="flex items-center gap-2 text-[13px] text-slate-500">
          <span>{on ? "Shown" : "Hidden"}</span>
          <Switch checked={on} onCheckedChange={onToggle} />
        </label>
      </div>
      <div className={`space-y-4 p-4 ${on ? "" : "opacity-50"}`}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function CtaEditor({ label, value = {}, onChange }) {
  return (
    <div className="space-y-2 rounded-lg border border-slate-100 p-2">
      <div className="text-[12px] font-medium text-slate-500">{label}</div>
      <Input value={value?.label || ""} onChange={(e) => onChange({ ...value, label: e.target.value })} placeholder="Button text" />
      <Input value={value?.href || ""} onChange={(e) => onChange({ ...value, href: e.target.value })} placeholder="Link (e.g. /properties)" />
    </div>
  );
}

// Add/remove/reorder list of items with a custom row renderer.
function ItemsEditor({ items, onChange, renderItem, makeItem, addLabel }) {
  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, makeItem()]);
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={idx} className="rounded-lg border border-slate-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1 text-slate-300">
              <GripVertical className="h-4 w-4" />
              <button type="button" onClick={() => move(idx, -1)} className="px-1 text-[12px] text-slate-400 hover:text-slate-700">↑</button>
              <button type="button" onClick={() => move(idx, 1)} className="px-1 text-[12px] text-slate-400 hover:text-slate-700">↓</button>
            </div>
            <button type="button" onClick={() => remove(idx)} className="text-slate-400 hover:text-red-500" aria-label="Remove">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          {renderItem(item, (patch) => update(idx, patch), idx)}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add}>
        <Plus className="h-3.5 w-3.5" /> {addLabel}
      </Button>
    </div>
  );
}
